/**
 * PREFLIGHT — the single, pure gate every send passes BEFORE the scripted
 * answer is produced (frames E-L of "Sakhtar AI Assistant.dc.html").
 *
 * No React, no I/O, no timers: `useChatSession` feeds it the live values and
 * acts on the verdict, and the unit test drives it directly. It lives in its own
 * file (and is re-exported from `useChatSession.ts`) only so it stays importable
 * under `node --experimental-strip-types`.
 *
 * ---------------------------------------------------------------------------
 * ORDER OF PRECEDENCE (first match wins)
 * ---------------------------------------------------------------------------
 *   1. user not permitted   `forcedState === 'not-permitted'`  -> frame L
 *   2. tenant suspended     `forcedState === 'suspended'`      -> frame K
 *   3. out of tokens        balance < the question's estimate  -> frame I / I-2
 *   4. service error        `forcedState === 'error'`          -> frame J
 *   5. ambiguity            the question is unclear            -> frame E
 *   6. access               a needed module is not granted     -> frame F (partial
 *                           answer) or a plain refusal notice; both ALLOW the send
 *   7. heavy confirmation   an expensive analysis              -> frame G
 *   otherwise               allow -> frame H (analyzing) -> answer
 *
 * Why this order: who you are (L, K) beats what you have (I), which beats what
 * the service does (J), which beats how well the question is phrased (E). The
 * access check (F) only changes WHAT is answered, so it precedes the cost
 * confirmation (G): a partial answer is never "heavy". Nothing here charges
 * tokens - the spend happens only after an answer, in `useChatSession`.
 *
 * Authorization stays in the backend: `forcedState` stands in for the backend's
 * verdict in this preview, and `granted` is the caller's REAL capability scope.
 */
import {
  detectAmbiguity,
  estimateTokens,
  heavyReason,
  HEAVY_ESTIMATE_TOKENS,
  ESTIMATE_BASE_TOKENS,
  planAnswer,
  type AnswerPlan,
} from './engine.ts';
import type { AiForcedState } from '../aiPreview.js';
import type { AssistantMessage, ReplaySpec, ScopeKey } from './types.ts';

/** Cheapest possible question (the base estimate): below this balance nothing can be sent at all. */
export const MIN_QUESTION_TOKENS = ESTIMATE_BASE_TOKENS;

/** True when no question at all can be afforded (frame I's persistent state: disabled composer). */
export function isOutOfTokens(balance: number): boolean {
  return balance < MIN_QUESTION_TOKENS;
}

export interface PreflightInput {
  /** Id for the message the verdict may produce (the caller allocates it). */
  readonly messageId: string;
  /** The question: request, bubble text, composer scope and period. */
  readonly replay: ReplaySpec;
  /** Modules the caller may really read. */
  readonly granted: readonly ScopeKey[];
  readonly forcedState: AiForcedState;
  /** Integer tokens left (sample wallet). */
  readonly tokenBalance: number;
  /** Real `tenant.subscription.manage`. */
  readonly canPurchase: boolean;
  /** The question is the continuation of a clarification - skip the ambiguity check. */
  readonly clarified?: boolean;
  /** The user already pressed «اجرا» on the heavy-analysis card - skip it. */
  readonly confirmedHeavy?: boolean;
  /** Failure counter carried by a retry (frame J). */
  readonly attempt?: number;
}

export type PreflightResult =
  | {
      readonly allow: true;
      /** How the request will be answered (full / partial / refused / unscripted). */
      readonly plan: AnswerPlan;
      /** Modules the analyzing state names. */
      readonly modules: readonly ScopeKey[];
      /** Integer estimate for this question (heavy analyses are never below the design's figure). */
      readonly estimate: number;
    }
  /**
   * Block or divert the send: `message` is appended after the user's bubble
   * instead of a scripted answer. Nothing was answered and nothing is charged.
   */
  | { readonly allow: false; readonly message: AssistantMessage };

export function preflight(input: PreflightInput): PreflightResult {
  const { messageId, replay, granted } = input;
  const plan = planAnswer(replay.request, granted);
  const reason = heavyReason(plan);
  const base = estimateTokens(replay.userText, replay.selected.length);
  const estimate = reason ? Math.max(base, HEAVY_ESTIMATE_TOKENS) : base;
  const blocked = (message: AssistantMessage): PreflightResult => ({ allow: false, message });
  const role = 'assistant' as const;

  // 1 + 2: the whole assistant is unavailable.
  if (input.forcedState === 'not-permitted') {
    return blocked({ id: messageId, role, kind: 'denied', reason: 'not-permitted' });
  }
  if (input.forcedState === 'suspended') {
    return blocked({ id: messageId, role, kind: 'denied', reason: 'suspended' });
  }

  // 3: the balance does not cover this question (a real integer comparison).
  if (input.tokenBalance < estimate) {
    return blocked({
      id: messageId,
      role,
      kind: 'out-of-tokens',
      estimate,
      balance: input.tokenBalance,
      canPurchase: input.canPurchase,
    });
  }

  // 4: the service failed - no tokens charged, the question is kept for a retry.
  if (input.forcedState === 'error') {
    return blocked({ id: messageId, role, kind: 'error', replay, attempt: input.attempt ?? 1 });
  }

  // 5: ambiguity - typed prompts only.
  if (!input.clarified && replay.request.type === 'prompt') {
    const clarify = detectAmbiguity(replay.request.text, replay.selected, granted);
    if (clarify) {
      return blocked({
        id: messageId,
        role,
        kind: 'clarify',
        question: clarify.question,
        options: clarify.options,
        selected: replay.selected,
        period: replay.period,
      });
    }
  }

  // 6 + 7: access is resolved by the plan (partial / refused answers are still
  // sent); only a fully answerable heavy question needs the confirmation.
  if (reason && !input.confirmedHeavy) {
    return blocked({
      id: messageId,
      role,
      kind: 'confirm-heavy',
      estimate,
      balance: input.tokenBalance,
      reason,
      status: 'pending',
      replay,
    });
  }

  return {
    allow: true,
    plan,
    modules: plan.modules.length > 0 ? plan.modules : replay.selected,
    estimate,
  };
}
